import React, { useMemo } from 'react'
import { Button, debounce, useTheme } from '@mui/material'
import { cloneDeep, union, difference } from 'lodash'

import { ItemLabel } from '../../../../components/styled'
import { FlexGrow } from '../../../../pages/CartComponents'
import { addGST } from '../../../../util/addGst'
import { CartItem } from '../../../cart/storage/cart.types'
import { SideContainer } from '../../../products/components/ProductCard'
import { CartItemCard } from '../../../products/components/ProductCard/CartItemCard'
import { CounterView } from '../../../products/components/ProductCard/ProductCardParent'
import { Quote, QuoteStatus } from '../../storage/types'
import {
  calculateNewDiscountedPriceExcGst,
  calculateNewBasePriceExcGst,
  calculatePriceForNewQuantity,
} from '../../utils/calculateTotal'
import { QuoteDetailInfo } from './QuoteDetailInfo'
import { QuoteProductCard } from './QuoteProductCard'
import { useSelector } from 'react-redux'
import { selectCurrentFarmerAccount } from '../../../azure-b2c/storage'
import { EditPriceModal } from '../../../cart/components/EditPriceModal'

export interface QuoteDetailsProductsProps {
  quote: Quote
  originalProducts: CartItem[]
  products: CartItem[]
  setProducts: (products: CartItem[]) => void
}

export const QuoteDetailsProducts = ({
  quote,
  originalProducts,
  products,
  setProducts,
}: QuoteDetailsProductsProps) => {
  const theme = useTheme()
  const account = useSelector(selectCurrentFarmerAccount)

  const [openEditPriceModal, setOpenEditPriceModal] = React.useState(false)
  const [editPriceItem, setEditPriceItem] = React.useState<CartItem>()

  const checkProductQuantity = (productId: number) => {
    const originalProduct = originalProducts.find((p) => p.productId === productId)
    const product = products.find((p) => p.productId === productId)

    if (originalProduct && product) {
      if (originalProduct.quantity > product.quantity) {
        changeProductQuantity(originalProduct.quantity, productId)
      }
    }
  }

  const debounceCheckProductQuantity = debounce(checkProductQuantity, 1000)

  const changeProductQuantity = (value: number, productId: number) => {
    const originalProduct = originalProducts.find((p) => p.productId === productId)
    const product = products.find((p) => p.productId === productId)

    if (originalProduct && product) {
      if (!account?.hasTfoAccess) debounceCheckProductQuantity(productId)
      const discountedPriceExcGst = calculateNewDiscountedPriceExcGst(product, value)
      product.discountedPriceExcGst = discountedPriceExcGst
      product.discountedPriceIncGst = addGST(discountedPriceExcGst)
      product.basePriceExcGst = calculateNewBasePriceExcGst(product, value)
      product.quantity = value
      setProducts(cloneDeep(products))
    }
  }

  const removeItem = (item: CartItem) => {
    setProducts(cloneDeep(products.filter((p) => p.productId !== item.productId)))
  }

  const restoreItem = (item: CartItem) => {
    setProducts(cloneDeep([...products, item]))
  }

  const renderProduct = (item: CartItem, index: number, status: QuoteStatus) => {
    if (
      QuoteStatus.Offered === status ||
      (QuoteStatus.Requested === status && account?.hasTfoAccess)
    ) {
      const originalProduct = originalProducts.find((p) => p.productId === item.productId)
      const tfoCanDecreaseQuantity = account?.hasTfoAccess && status === QuoteStatus.Requested
      const hideDownCount = tfoCanDecreaseQuantity
        ? false
        : (originalProduct && originalProduct?.quantity >= item.quantity) || false
      const handleOpenEditPriceModal = account?.hasTfoAccess
        ? () => {
            setEditPriceItem(item)
            setOpenEditPriceModal(true)
          }
        : undefined
      return (
        <QuoteProductCard
          key={`{product.productId}_${index}`}
          item={{ ...item }}
          enableRemove={!quote?.isLocked}
          hideDownCount={hideDownCount}
          onChangeQuantity={(value) => {
            changeProductQuantity(value, item.productId)
          }}
          priceEditable={account?.hasTfoAccess || false}
          handleOpenEditPriceModal={handleOpenEditPriceModal}
          onRemove={
            products.length <= 1
              ? undefined
              : () => {
                  removeItem(item)
                }
          }
        />
      )
    } else {
      return (
        <CartItemCard
          key={`{product.productId}_${index}`}
          item={item}
          onRemove={undefined}
          onChangeVariant={undefined}
          enableVariants={true}
          handleOpenEditPriceModal={undefined}
          priceEditable={false}
          renderSideContainer={
            <SideContainer>
              <CounterView>
                <ItemLabel variant='body2'>{`Quantity ${item.quantity}`}</ItemLabel>
              </CounterView>
            </SideContainer>
          }
          showUrgentDeliveryUnavailableMessage={false}
        />
      )
    }
  }

  const renderMissingProduct = (productId: number, index: number) => {
    const product = originalProducts.find((p) => p.productId === productId)

    if (product)
      return (
        <CartItemCard
          key={`original_{product.productId}_${index}`}
          sx={{ backgroundColor: theme.colors.grey100 }}
          item={product}
          onRemove={undefined}
          onChangeVariant={undefined}
          enableVariants={true}
          handleOpenEditPriceModal={undefined}
          priceEditable={false}
          renderSideContainer={
            <SideContainer>
              <Button variant='outlined' onClick={() => restoreItem(product)}>
                Restore
              </Button>
            </SideContainer>
          }
          showUrgentDeliveryUnavailableMessage={false}
        />
      )
  }

  const renderRemovedProducts = useMemo(() => {
    const productIds = products.map((p) => p.productId)
    const originalProductIds = originalProducts.map((p) => p.productId)
    if (union(productIds, originalProductIds).length === productIds.length) return null

    const missingProductIds = difference(originalProductIds, productIds)
    return missingProductIds.map((id, index) => renderMissingProduct(id, index))
  }, [products, originalProducts])

  const renderProducts = useMemo(() => {
    if (quote) {
      return products.map((product, index) => renderProduct(product, index, quote.status))
    }
  }, [products, quote?.status])

  const onRevertEditedPrice = async ({ productId }: CartItem) => {
    const originalProduct = originalProducts.find((p) => p.productId === productId)
    const product = products.find((p) => p.productId === productId)

    if (originalProduct && product) {
      const discountedPriceExcGst = calculatePriceForNewQuantity(
        originalProduct.discountedPriceExcGst,
        originalProduct.quantity,
        product.quantity,
      )
      product.discountedPriceExcGst = discountedPriceExcGst
      product.discountedPriceIncGst = addGST(discountedPriceExcGst)
      product.basePriceExcGst = calculatePriceForNewQuantity(
        originalProduct.basePriceExcGst,
        originalProduct.quantity,
        product.quantity,
      )
      product.editedPriceExcGst = undefined
      setProducts(cloneDeep(products))
    }
  }

  const onApplyEditedPrince = (
    editedPrice: number,
    editedQuantity: number,
    { productId }: CartItem,
  ) => {
    const product = products.find((p) => p.productId === productId)

    if (product) {
      product.editedPriceExcGst = editedPrice
      product.quantity = editedQuantity
      const discountedPriceExcGst = editedPrice * editedQuantity
      product.discountedPriceExcGst = discountedPriceExcGst
      product.discountedPriceIncGst = addGST(discountedPriceExcGst)
      setProducts(cloneDeep(products))
    }
  }

  return (
    <FlexGrow>
      {renderProducts}
      {renderRemovedProducts}
      <QuoteDetailInfo quote={quote} products={products} />

      <EditPriceModal
        item={editPriceItem}
        open={openEditPriceModal}
        setOpen={setOpenEditPriceModal}
        onApplyEditedPrince={onApplyEditedPrince}
        onRevertEditedPrice={onRevertEditedPrice}
      />
    </FlexGrow>
  )
}
